import { KeyVisual as KeyVisualComponent } from '@pixum/key-visual';
import { Image, IMAGE_LOADING, ImageFetchPriority } from '../../image';
import { Voucher } from '../../voucher';
import { KeyVisualActions, KeyVisualPicture, KeyVisualRating, KeyVisualVideo } from '../partials';
import type { KeyVisualNonClassicProps } from '../types';

/**
 * KeyVisualNonClassic component - Non-classic variant for key visuals.
 *
 * Renders high-contrast, highlight, and other non-classic key visual types.
 *
 * @param props - KeyVisual variant component properties
 * @returns Rendered non-classic key visual variant
 */
export function KeyVisualNonClassic(props: KeyVisualNonClassicProps) {
  const {
    keyVisual: {
      id,
      emphasis,
      image,
      links = [],
      title,
      subtitle,
      pictureData,
      imageVariantsByDeviceSize,
      constants,
      video
    },
    device,
    voucher,
    hasHotspots,
    featuredAward,
    awardOrientation,
    text,
    countdown
  } = props;

  const hasVideo = video && !!video?.src;
  const link = links[0];
  const shouldShowCustomerRatings = props?.customerReviews && props.customerReviews.count > 0;

  return (
    <KeyVisualComponent
      id={id}
      actions={<KeyVisualActions buttons={links} device={device} constants={constants} />}
      awardImage={
        featuredAward ? (
          <Image
            device={device}
            loading={IMAGE_LOADING.EAGER}
            fetchPriority={ImageFetchPriority.High}
            image={featuredAward.image}
          />
        ) : undefined
      }
      awardOrientation={awardOrientation}
      emphasis={emphasis}
      headlineText={title}
      media={
        hasVideo ? (
          <KeyVisualVideo url={link?.url} label={link?.title} video={video} />
        ) : (
          <KeyVisualPicture
            image={image}
            pictureData={pictureData}
            imageVariantsByDeviceSize={imageVariantsByDeviceSize}
            device={device}
            hasHotSpots={hasHotspots}
          />
        )
      }
      rating={shouldShowCustomerRatings ? <KeyVisualRating customerReviews={props.customerReviews} /> : undefined}
      subHeadlineText={subtitle}
      supportingText={text}
      countdown={countdown}
      withScale={!hasHotspots}
      voucher={
        voucher && (
          <Voucher sources={['props', 'url', 'localStorage']} variant="extended" addToCart={true} {...voucher} />
        )
      }
    />
  );
}
